'use client';

import React, { MutableRefObject, PropsWithChildren, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import {
  registerPerformanceLabLenis,
  unregisterPerformanceLabLenis,
} from 'utils/performanceLabLenisRegistry';
import './PerformanceLabLenisRoot.module.css';

gsap.registerPlugin(ScrollTrigger);

const lenisModulePromise = import('lenis');

const LENIS_LERP = 0.12;
const LENIS_WHEEL_MULTIPLIER = 0.9;
const LENIS_TOUCH_MULTIPLIER = 0.95;
const GSAP_LAG_SMOOTHING_THRESHOLD = 500;
const GSAP_LAG_SMOOTHING_ADJUSTED_LAG = 33;

export type PerformanceLabLenisRootProps = PropsWithChildren<{
  /**
   * Optional batched mask/layout updater from the page so `--pl-white-mask-*`
   * stays in sync with Lenis-driven scroll, not only raw window scroll.
   */
  scrollSyncRef?: MutableRefObject<(() => void) | null>;
}>;

export const PerformanceLabLenisRoot: React.FC<PerformanceLabLenisRootProps> = ({
  children,
  scrollSyncRef,
}) => (
  <PerformanceLabLenisInner scrollSyncRef={scrollSyncRef}>
    {children}
  </PerformanceLabLenisInner>
);

const PerformanceLabLenisInner: React.FC<PerformanceLabLenisRootProps> = ({
  children,
  scrollSyncRef,
}) => {
  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMotion.matches) return;

    let cancelled = false;
    let lenisInstance: InstanceType<typeof import('lenis').default> | null = null;
    let tickerCb: ((time: number) => void) | null = null;
    let refreshRafId = 0;

    void lenisModulePromise.then(({ default: Lenis }) => {
      if (cancelled) return;

      const instance = new Lenis({
        autoRaf: false,
        lerp: LENIS_LERP,
        wheelMultiplier: LENIS_WHEEL_MULTIPLIER,
        touchMultiplier: LENIS_TOUCH_MULTIPLIER,
      });
      if (cancelled) {
        instance.destroy();
        return;
      }

      lenisInstance = instance;
      registerPerformanceLabLenis(instance);

      ScrollTrigger.scrollerProxy(document.documentElement, {
        scrollTop(value) {
          if (arguments.length && value !== undefined) {
            instance.scrollTo(value, { immediate: true });
          }
          return instance.scroll;
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          };
        },
        pinType:
          document.body.style.transform ? 'transform' : 'fixed',
      });

      instance.on('scroll', () => {
        ScrollTrigger.update();
        scrollSyncRef?.current?.();
      });

      tickerCb = (time: number) => {
        instance.raf(time * 1000);
      };
      gsap.ticker.add(tickerCb);
      gsap.ticker.lagSmoothing(
        GSAP_LAG_SMOOTHING_THRESHOLD,
        GSAP_LAG_SMOOTHING_ADJUSTED_LAG,
      );

      refreshRafId = window.requestAnimationFrame(() => {
        refreshRafId = window.requestAnimationFrame(() => {
          refreshRafId = 0;
          ScrollTrigger.refresh();
        });
      });
    });

    return () => {
      cancelled = true;
      if (refreshRafId) window.cancelAnimationFrame(refreshRafId);
      if (tickerCb) gsap.ticker.remove(tickerCb);
      gsap.ticker.lagSmoothing(
        GSAP_LAG_SMOOTHING_THRESHOLD,
        GSAP_LAG_SMOOTHING_ADJUSTED_LAG,
      );
      if (lenisInstance) {
        unregisterPerformanceLabLenis(lenisInstance);
        lenisInstance.destroy();
      }
      ScrollTrigger.scrollerProxy(document.documentElement, {});
      ScrollTrigger.refresh();
    };
  }, [scrollSyncRef]);

  return <>{children}</>;
};
